/**
 * Clinical shortcuts and smart templates — the deterministic half.
 *
 * A clinician picks quick-select options and fills a few blanks; this module
 * turns that into sentences. No model is involved and nothing is inferred:
 *
 *   · An option that was not selected never appears.
 *   · A pattern sentence appears only when every blank in it was filled. An
 *     unfilled finding drops its sentence — it is never written as "normal",
 *     "no abnormality" or anything else.
 *   · A shortcut that matches more than one template returns the choices, so
 *     the clinician picks; it never guesses.
 *   · Two options from the same exclusive group (say "No complaints" and
 *     "Complaint reported") are a conflict the clinician resolves; neither is
 *     written until they do.
 *
 * Pure, with no imports, because the SaaS imports it through the
 * `@dru/clinical-math` alias for its live preview and the API runs the same
 * function as the authority.
 */

export const TEMPLATE_CONTEXTS = [
  'CONSULTATION', 'MEDICAL_HISTORY', 'PRESCRIPTION', 'LAB_REMARK', 'CERTIFICATE',
  'STATION_NOTE', 'REFERRAL', 'INVESTIGATION', 'CASUALTY', 'CORRECTION',
] as const;
export type TemplateContext = (typeof TEMPLATE_CONTEXTS)[number];

export const TEMPLATE_CONTEXT_LABEL: Record<TemplateContext, string> = {
  CONSULTATION: 'Physician consultation',
  MEDICAL_HISTORY: 'Medical history',
  PRESCRIPTION: 'Prescription',
  LAB_REMARK: 'Laboratory remark',
  CERTIFICATE: 'Fitness certificate',
  STATION_NOTE: 'Station note',
  REFERRAL: 'Referral / follow-up',
  INVESTIGATION: 'ECG / vision / hearing / lung / X-ray review',
  CASUALTY: 'Casualty treatment',
  CORRECTION: 'Report correction',
};

/**
 * Who may use a composer in each context: the clinical write permission of
 * the field it fills. HR holds none of these — `treatment.write` is
 * deliberately absent, because HR roles hold it and casualty wording is a
 * clinician's — so no HR login is offered a clinical composer.
 */
export const CONTEXT_PERMISSIONS: Record<TemplateContext, readonly string[]> = {
  CONSULTATION: ['physician.review', 'review.draft', 'encounter.station-enter', 'report.verify-clinical'],
  MEDICAL_HISTORY: ['encounter.station-enter', 'physician.review', 'review.draft', 'report.verify-clinical'],
  PRESCRIPTION: ['physician.review', 'review.draft', 'report.verify-clinical'],
  LAB_REMARK: ['lab.result-enter', 'lab.result-validate', 'lab.result-release'],
  CERTIFICATE: ['physician.certify'],
  STATION_NOTE: ['encounter.station-enter'],
  REFERRAL: ['physician.review', 'review.draft', 'report.verify-clinical'],
  INVESTIGATION: ['physician.review', 'lab.result-validate', 'report.verify-clinical', 'encounter.station-enter'],
  CASUALTY: ['physician.review', 'report.verify-clinical'],
  CORRECTION: ['physician.amend', 'lab.result-amend'],
};

export const FIELD_TYPES = ['text', 'number', 'unit', 'duration', 'date', 'choice'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const DURATION_UNITS = ['hour', 'day', 'week', 'month', 'year'] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];

export interface TemplateChoice {
  value: string;
  label: string;
  labelHi?: string;
  /** The words written into the sentence; the label when absent. */
  text?: string;
}

export interface TemplateField {
  key: string;
  label: string;
  labelHi?: string;
  type: FieldType;
  /** For `unit`: the unit written after the number ("mg", "mmHg"). */
  unit?: string;
  /** For `choice`. */
  choices?: TemplateChoice[];
  placeholder?: string;
}

export interface TemplateOption {
  code: string;
  label: string;
  labelHi?: string;
  /** The fixed full sentence this button writes. */
  sentence: string;
  /** Options sharing a group are mutually exclusive. */
  group?: string;
}

export interface TemplateShape {
  /** Patterns with `{field}` blanks. Each must contain at least one blank. */
  sentences: string[];
  fields: TemplateField[];
  options: TemplateOption[];
}

export type DurationValue = { amount: number | string; unit: DurationUnit | string };
export type FieldValue = string | number | DurationValue | null | undefined;

export interface RenderInput {
  values?: Record<string, FieldValue>;
  selected?: string[];
}

export interface RenderedSentence {
  source: 'option' | 'pattern';
  /** The option code, or the pattern's index. */
  ref: string;
  text: string;
}

export interface RenderResult {
  text: string;
  sentences: RenderedSentence[];
  /** Fields that were left blank, whose sentences were therefore dropped. */
  missing: string[];
  /** Fields whose value could not be read (a number that is not one). */
  invalid: string[];
  /** Groups where more than one exclusive option was selected — the clinician must choose. */
  conflicts: Array<{ group: string; codes: string[] }>;
  /** Selected codes the template does not have. */
  unknownOptions: string[];
}

const BLANK = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

const SMALL = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The blanks a pattern sentence uses, in order. */
export function blanksOf(pattern: string): string[] {
  return [...pattern.matchAll(BLANK)].map((m) => m[1]!);
}

function clean(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/** Strips one trailing full stop so a value never doubles the sentence's own. */
function bare(s: string): string {
  return clean(s).replace(/[.。]+$/, '');
}

function asNumber(v: FieldValue): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
}

function isBlank(v: FieldValue): boolean {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v === 'number') return false;
  if (typeof v === 'object') return v.amount == null || String(v.amount).trim() === '' || !v.unit;
  return true;
}

/** "two days", "one week", "14 days". */
export function durationWords(v: DurationValue): string | null {
  const n = asNumber(v.amount as FieldValue);
  const unit = String(v.unit ?? '').toLowerCase().replace(/s$/, '');
  if (n == null || n <= 0 || !(DURATION_UNITS as readonly string[]).includes(unit)) return null;
  const count = Number.isInteger(n) && n <= 10 ? SMALL[n]! : String(n);
  return `${count} ${unit}${n === 1 ? '' : 's'}`;
}

/** 2026-11-05 → "5 Nov 2026". No locale, so the API and the browser agree. */
export function dateWords(v: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${day} ${MONTHS[month - 1]} ${m[1]}`;
}

/** The words a field's value becomes, or null when blank / unreadable. */
function fieldWords(field: TemplateField, v: FieldValue): { words: string | null; invalid: boolean } {
  if (isBlank(v)) return { words: null, invalid: false };
  switch (field.type) {
    case 'text': {
      const w = typeof v === 'string' || typeof v === 'number' ? bare(String(v)) : '';
      return { words: w || null, invalid: false };
    }
    case 'number': {
      const n = asNumber(v);
      return n == null ? { words: null, invalid: true } : { words: String(n), invalid: false };
    }
    case 'unit': {
      const n = asNumber(v);
      if (n == null) return { words: null, invalid: true };
      return { words: field.unit ? `${n} ${field.unit}` : String(n), invalid: false };
    }
    case 'duration': {
      const d = typeof v === 'object' && v ? durationWords(v) : null;
      return d ? { words: d, invalid: false } : { words: null, invalid: true };
    }
    case 'date': {
      const d = typeof v === 'string' ? dateWords(v) : null;
      return d ? { words: d, invalid: false } : { words: null, invalid: true };
    }
    case 'choice': {
      const c = (field.choices ?? []).find((x) => x.value === String(v));
      return c ? { words: bare(c.text ?? c.label), invalid: false } : { words: null, invalid: true };
    }
    default:
      return { words: null, invalid: true };
  }
}

function sentenceCase(s: string): string {
  const t = clean(s);
  if (!t) return t;
  const first = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(first) ? first : `${first}.`;
}

/**
 * Fill a template. Selected options first, in the template's own order, then
 * every pattern sentence whose blanks are all filled.
 */
export function renderTemplate(shape: TemplateShape, input: RenderInput): RenderResult {
  const values = input.values ?? {};
  const selected = new Set(input.selected ?? []);
  const fields = new Map(shape.fields.map((f) => [f.key, f]));
  const codes = new Set(shape.options.map((o) => o.code));

  const unknownOptions = [...selected].filter((c) => !codes.has(c));

  const byGroup = new Map<string, string[]>();
  for (const o of shape.options) {
    if (!o.group || !selected.has(o.code)) continue;
    byGroup.set(o.group, [...(byGroup.get(o.group) ?? []), o.code]);
  }
  const conflicts = [...byGroup.entries()].filter(([, c]) => c.length > 1).map(([group, c]) => ({ group, codes: c }));
  const conflicted = new Set(conflicts.flatMap((c) => c.codes));

  const sentences: RenderedSentence[] = [];
  for (const o of shape.options) {
    if (!selected.has(o.code) || conflicted.has(o.code)) continue;
    const text = sentenceCase(o.sentence);
    if (text) sentences.push({ source: 'option', ref: o.code, text });
  }

  const missing = new Set<string>();
  const invalid = new Set<string>();
  shape.sentences.forEach((pattern, i) => {
    const blanks = blanksOf(pattern);
    // A pattern with no blank would be written whatever was chosen. Fixed
    // sentences are options, which have to be pressed.
    if (!blanks.length) return;
    let ok = true;
    const filled = pattern.replace(BLANK, (_m, key: string) => {
      const field = fields.get(key);
      if (!field) { ok = false; return ''; }
      const { words, invalid: bad } = fieldWords(field, values[key]);
      if (bad) invalid.add(key);
      if (words == null) { ok = false; if (!bad) missing.add(key); return ''; }
      return words;
    });
    if (ok) sentences.push({ source: 'pattern', ref: String(i), text: sentenceCase(filled) });
  });

  return {
    text: sentences.map((s) => s.text).join(' '),
    sentences,
    missing: [...missing],
    invalid: [...invalid],
    conflicts,
    unknownOptions,
  };
}

/** Problems with a template as authored. Empty when it can be saved. */
export function validateTemplateShape(shape: TemplateShape): string[] {
  const errors: string[] = [];
  const keys = new Set<string>();
  for (const f of shape.fields) {
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(f.key)) errors.push(`Field key "${f.key}" must be letters, digits or _`);
    if (keys.has(f.key)) errors.push(`Field "${f.key}" is defined twice`);
    keys.add(f.key);
    if (!(FIELD_TYPES as readonly string[]).includes(f.type)) errors.push(`Field "${f.key}" has an unknown type`);
    if (f.type === 'choice' && !(f.choices ?? []).length) errors.push(`Choice field "${f.key}" needs choices`);
    if (f.type === 'unit' && !f.unit) errors.push(`Unit field "${f.key}" needs its unit`);
  }
  const used = new Set<string>();
  shape.sentences.forEach((s, i) => {
    const blanks = blanksOf(s);
    if (!blanks.length) errors.push(`Sentence ${i + 1} has no blank; a fixed sentence must be a quick-select option`);
    for (const b of blanks) {
      if (!keys.has(b)) errors.push(`Sentence ${i + 1} uses {${b}}, which is not a field`);
      used.add(b);
    }
  });
  for (const k of keys) if (!used.has(k)) errors.push(`Field "${k}" is not used in any sentence`);
  const codes = new Set<string>();
  for (const o of shape.options) {
    if (codes.has(o.code)) errors.push(`Option "${o.code}" is defined twice`);
    codes.add(o.code);
    if (!clean(o.sentence)) errors.push(`Option "${o.code}" has no sentence`);
    if (blanksOf(o.sentence).length) errors.push(`Option "${o.code}" has a blank; options are fixed sentences`);
  }
  if (!shape.sentences.length && !shape.options.length) errors.push('A template needs at least one option or sentence');
  return errors;
}

// ── Shortcuts ───────────────────────────────────────────────────────

export interface ShortcutEntry {
  id: string;
  shortcut: string;
  title: string;
  kind: 'template' | 'favourite';
}

export type ShortcutResolution<E extends ShortcutEntry = ShortcutEntry> =
  | { kind: 'none' }
  | { kind: 'match'; entry: E }
  | { kind: 'choices'; entries: E[] };

/** "/History " → "/history". */
export function normaliseShortcut(s: string): string {
  const t = s.trim().toLowerCase();
  if (!t) return '';
  return t.startsWith('/') ? t : `/${t}`;
}

/**
 * What a typed shortcut opens. An exact match wins when it is the only one;
 * otherwise everything it is a prefix of. One candidate opens; several are
 * returned for the clinician to choose; none is none.
 */
export function resolveShortcut<E extends ShortcutEntry>(typed: string, entries: E[]): ShortcutResolution<E> {
  const q = normaliseShortcut(typed);
  if (q.length < 2) return { kind: 'none' };
  const exact = entries.filter((e) => normaliseShortcut(e.shortcut) === q);
  if (exact.length === 1) return { kind: 'match', entry: exact[0]! };
  if (exact.length > 1) return { kind: 'choices', entries: exact };
  const prefix = entries.filter((e) => normaliseShortcut(e.shortcut).startsWith(q));
  if (prefix.length === 1) return { kind: 'match', entry: prefix[0]! };
  if (prefix.length > 1) return { kind: 'choices', entries: prefix };
  return { kind: 'none' };
}

/**
 * Words an AI draft must not introduce on its own. Checked against the
 * clinician's source text: a term in the draft that the source never said
 * is flagged for the clinician, never silently kept.
 */
const GUARDED_TERMS: Array<[RegExp, string]> = [
  [/\bnormal\b/i, 'normal'],
  [/\bwithin normal limits\b|\bwnl\b/i, 'within normal limits'],
  [/\bno abnormalit(y|ies)\b|\bnad\b/i, 'no abnormality detected'],
  [/\bunremarkable\b/i, 'unremarkable'],
  [/\b(un)?fit\b/i, 'fit / unfit'],
  [/\bdiagnos(is|ed)\b/i, 'diagnosis'],
  [/\b(tab|tablet|cap|capsule|syrup|inj|injection)\b/i, 'a medicine'],
];

/** Guarded terms in `draft` that `source` does not contain. */
export function addedGuardedTerms(source: string, draft: string): string[] {
  const out: string[] = [];
  for (const [re, label] of GUARDED_TERMS) {
    if (re.test(draft) && !re.test(source)) out.push(label);
  }
  return out;
}
